<template>
  <b-modal
    title="Bearbeiten"
    cancel-title="Abbrechen"
    :ok-disabled="currentRecipe.title === ''"
    @ok.prevent="saveChanges"
    @cancel.prevent="close"
    scrollable
    v-model="value"
    v-if="currentRecipe"
  >
    <template slot="modal-header">
      <small v-if="currentRecipe.id > ''"
        >Eintrag bearbeiten: <b>{{ currentRecipe.title }}</b></small
      >
      <small v-else>Neuer Eintrag</small>
    </template>
    <div class="form-group">
      <label for="frm-recipeName" class="col-form-label-sm">Name</label>
      <input
        id="frm-recipeName"
        type="text"
        class="form-control form-control-sm"
        v-model="currentRecipe.title"
      />
    </div>
    <b-form-group label-size="sm" label="Typen">
      <!-- prop `add-on-change` is needed to enable adding tags vie the `change` event -->
      <b-form-tags
        v-model="currentRecipe.type"
        size="sm"
        add-on-change
        no-outer-focus
      >
        <template
          v-slot="{ tags, inputAttrs, inputHandlers, disabled, removeTag }"
        >
          <ul v-if="tags.length > 0" class="list-inline d-inline-block mb-2">
            <li v-for="tag in tags" :key="tag" class="list-inline-item">
              <b-form-tag
                @remove="removeTag(tag)"
                :title="tag"
                :disabled="disabled"
                :variant="getItemVariantColor(tag)"
                >{{ tag }}</b-form-tag
              >
            </li>
          </ul>
          <b-form-select
            size="sm"
            v-bind="inputAttrs"
            v-on="inputHandlers"
            :disabled="disabled || availableTypes.length === 0"
            :options="availableTypes"
          >
            <template v-slot:first>
              <!-- This is required to prevent bugs with Safari -->
              <option disabled value="">Typ wählen</option>
            </template>
          </b-form-select>
        </template>
      </b-form-tags>
    </b-form-group>
    <b-form-group label-size="sm" label="Tags">
      <!-- prop `add-on-change` is needed to enable adding tags vie the `change` event -->
      <b-form-tags
        input-id="frm-tags"
        v-model="currentRecipe.tags"
        separator=" ,;"
        placeholder=""
        no-add-on-enter
        size="sm"
        add-button-text="+"
        duplicate-tag-text="Doppelte Tag(s)"
      ></b-form-tags>
    </b-form-group>
    <div class="form-group">
      <label for="frm-ingredients" class="col-form-label-sm">Zutaten</label>
      <div
        v-for="(ingredient, index) in currentRecipe.recipe"
        :key="'frm-ingredient-' + currentRecipe.id + '-' + index"
      >
        <b-form-row class="mb-1">
          <b-col cols="2">
            <input
              class="form-control form-control-sm p-1"
              v-model="ingredient.amount"
            />
          </b-col>
          <b-col cols="4">
            <b-form-select
              size="sm"
              class="p-1"
              v-model="ingredient.unit"
              :options="unitOptions"
            ></b-form-select>
          </b-col>
          <b-col cols="5">
            <input
              class="form-control form-control-sm p-1"
              v-model="ingredient.title"
            />
          </b-col>
          <b-col>
            <b-btn
              size="sm"
              variant="light"
              class="border px-1 py-0"
              @click="removeIngredient(index)"
            >
              <b-icon-x class="text-danger" />
            </b-btn>
          </b-col>
        </b-form-row>
      </div>
      <b-btn
        block
        size="sm"
        variant="secondary"
        class="mt-2"
        @click="addIngredient"
      >
        <b-icon-plus-circle-fill class="mr-2" />Neue Zutat
      </b-btn>
    </div>
    <div class="form-group">
      <label for="frm-instructions" class="col-form-label-sm"
        >Zubereitung</label
      >
      <div
        v-for="(instruction, index) in currentRecipe.instructions"
        :key="'frm-instruction-' + currentRecipe.id + '-' + index"
      >
        <b-form-row class="mb-1">
          <b-col cols="11">
            <b-form-textarea
              v-model="instruction.title"
              placeholder="Was geschieht in diesem Schritt?"
              rows="1"
              max-rows="6"
            ></b-form-textarea>
          </b-col>
          <b-col>
            <b-btn
              size="sm"
              variant="light"
              class="border px-1 py-0"
              @click="removeInstruction(index)"
            >
              <b-icon-x class="text-danger" />
            </b-btn>
          </b-col>
        </b-form-row>
      </div>
      <b-btn
        block
        size="sm"
        variant="secondary"
        class="mt-2"
        @click="addInstruction"
      >
        <b-icon-plus-circle-fill class="mr-2" />Neuer Schritt
      </b-btn>
    </div>
    <b-btn
      block
      size="sm"
      variant="danger"
      class="my-2"
      v-if="currentRecipe.id !== ''"
      @click="deleteRecipe"
      ><b-icon-trash class="mr-2" />Eintrag löschen</b-btn
    >
  </b-modal>
</template>

<script>
import { mapGetters } from "vuex";
import { Ingredient, UnitOptions, Types, deleteRecipe } from "@/class/recipe";
import axios from "axios";
export default {
  name: "EditModal",
  props: {
    value: Boolean,
    currentRecipe: Object
  },
  data() {
    return {
      types: Types,
      unitOptions: UnitOptions
    };
  },
  computed: {
    availableTypes() {
      return this.types.filter(
        opt => this.currentRecipe.type.indexOf(opt) === -1
      );
    },
    ...mapGetters(["getItemVariantColor"])
  },
  methods: {
    saveChanges() {
      axios
        .post(process.env.VUE_APP_API_URL + "?recipe", {
          cmd: "upsert",
          recipe: this.currentRecipe
        })
        .then(r => {
          console.log(r.data);
        })
        .finally(() => {
          this.$store.commit("SHOW_EDITMODAL", false);
        });
    },
    close() {
      this.$store.commit("SHOW_EDITMODAL", false);
    },
    deleteRecipe() {
      this.$bvModal
        .msgBoxConfirm("Möchten Sie diesen Eintrag wirklich löschen?", {
          cancelTitle: "Abbrechen",
          okTitle: "Ja",
          okVariant: "danger"
        })
        .then(value => {
          if (value === true) {
            deleteRecipe(this.currentRecipe.id).then(() => {
              this.$store.commit("SHOW_EDITMODAL", false);
            });
          }
        })
        .catch(err => {
          // An error occurred
          console.log(err);
        });
    },
    addIngredient() {
      this.currentRecipe.recipe.push(new Ingredient("", "", ""));
    },
    removeIngredient(index) {
      this.currentRecipe.recipe.splice(index, 1);
    },
    addInstruction() {
      this.currentRecipe.instructions.push({ id: 0, title: "" });
    },
    removeInstruction(index) {
      this.currentRecipe.instructions.splice(index, 1);
    }
  }
};
</script>

<style></style>
